import { useCallback, useEffect, useRef } from 'react';
import { useForm, type StandardSchemaV1 } from '@tanstack/react-form';
import type { z } from 'zod';

export interface UseZodFormOptions<T> {
  schema: z.ZodType<T>;
  defaultValues: T;
  resetOnDefaultsChange?: boolean;
}

export type ZodFormValidateSuccess<T> = { success: true; data: T };
export type ZodFormValidateError<T> = { success: false; error: z.ZodError<T> };
export type ZodFormValidateResult<T> = ZodFormValidateSuccess<T> | ZodFormValidateError<T>;

/**
 * Generic form hook combining TanStack Form with Zod schema validation.
 */
export function useZodForm<T>(options: UseZodFormOptions<T>) {
  const { schema, defaultValues, resetOnDefaultsChange = false } = options;
  const isInitialMount = useRef(true);
  const standardSchema = schema as unknown as StandardSchemaV1<T, T>;

  const form = useForm({
    defaultValues,
    validators: { onSubmit: standardSchema },
  });

  useEffect(() => {
    if (resetOnDefaultsChange) {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      form.reset(defaultValues);
    }
  }, [defaultValues, form, resetOnDefaultsChange]);

  const validateDraft = useCallback((): ZodFormValidateResult<T> => {
    return schema.safeParse(form.state.values) as ZodFormValidateResult<T>;
  }, [form, schema]);

  return { form, validateDraft };
}
